import { jitterFactory } from '@yandex-int/messenger.utils';
import { call, cancelled, delay, FixedTask, fork, put, race, take } from 'typed-redux-saga';

import { ErrorPageType } from '../../../../../shared/components/error-page/types';
import { setErrorPageAction } from '../../../../../shared/features/config';
import { broadcastManager, goloomSdk } from '../../../../../shared/features/goloom-sdk';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import offlineMonitor from '../../../../../shared/helpers/offline';
import { ConferenceStatus } from '../../../constants';
import { setOnline } from '../../offline';
import { networkStateOffline } from '../../offline/slice';
import { reconnectConference, setConferenceStatus } from '../slice';
import { updateConferenceConfig } from './conference-config';
import { initConferenceSaga } from './init';
import { join } from './join';
import { participantsAddedSaga } from './participants';

const MAX_RECONNECT_ATTEMPT_COUNT = 5;
const BASE_RECONNECT_TIMEOUT = 1000;
const RECONNECT_DISPERSION = 300;

const reconnectAttemptDelay = jitterFactory(BASE_RECONNECT_TIMEOUT, RECONNECT_DISPERSION);

const logger = createLogger('goloom:reconnect');

interface Options {
    roomId: string;
    searchString?: string;
    displayName?: string;
}

function* reconnectWorker(attemptCount: number, onSuccess: () => void, options: Options): Generator {
    yield put(setConferenceStatus(ConferenceStatus.FAILED));

    if (attemptCount >= MAX_RECONNECT_ATTEMPT_COUNT) {
        yield put(
            setErrorPageAction({
                type: ErrorPageType.DEFAULT,
                reason: {
                    method: 'reconnectWorker',
                    message: 'Attempt count more than MAX_RECONNECT_ATTEMPT_COUNT',
                },
            })
        );
        yield call([goloomSdk, goloomSdk.destroy]);
        yield put(setConferenceStatus(ConferenceStatus.DISCONNECTED));
        return;
    }

    const { roomId, searchString, displayName } = options;

    if (attemptCount > 0) {
        yield delay(reconnectAttemptDelay());
        // При закрытии-открытии крышки ноута не вызываются события
        // offline и online, поэтому нужен пинг.
        // Кроме того RTC соединение может прерваться раньше, чем произойдёт офлайн.
        // Поэтому пинг нужен тут, а не в [createOfflineChannel](../offline.ts).
        yield* call([offlineMonitor, 'pingUntilOnline']);
    }

    try {
        yield put(setConferenceStatus(ConferenceStatus.JOINING));

        const config = yield* call(updateConferenceConfig, roomId, searchString, displayName);

        yield* call(initConferenceSaga, { syncInitDevices: true, reinit: true });

        yield call([broadcastManager, broadcastManager.reinit], {
            uri: config.uri,
            broadcastUri: config.conference_state?.broadcast_data?.broadcast_uri,
            conferenceConfigOptions: {
                roomId,
                searchString,
                displayName,
            },
        });

        const participants = yield* call(join, config, roomId, searchString, displayName);

        if (participants) {
            const meParticipant = goloomSdk.getMe();

            if (meParticipant) {
                participants.push(meParticipant);
            }

            yield call(participantsAddedSaga, participants);
        }

        yield put(setConferenceStatus(ConferenceStatus.JOINED));
        yield call(setOnline);
        yield call(onSuccess);
    } catch (error) {
        logger.warn(error, {
            method: 'reconnectSaga',
        });
        yield put(setConferenceStatus(ConferenceStatus.FAILED));
        yield put(reconnectConference({ reason: 'Reconnect attempt error' }));
    } finally {
        if (yield* cancelled()) {
            yield call([goloomSdk, goloomSdk.destroy]);
            yield put(setConferenceStatus(ConferenceStatus.FAILED));
        }
    }
}

export function* reconnectWatchSaga(options: Options) {
    let attemptCount = 0;
    let task: FixedTask<void> | null = null;

    const handleSuccess = () => {
        attemptCount = 0;
    };

    while (true) {
        const [reconnect] = yield* race([take(reconnectConference), take(networkStateOffline)]);

        task?.cancel();

        if (reconnect) {
            logger.error(new Error('failure: hard reconnect'), {
                level: 'error',
                method: 'reconnectSaga',
                additional: {
                    kind: 'broken_connect_goloom',
                    retryN: attemptCount,
                    reason: reconnect.payload?.reason,
                },
            });

            attemptCount++;

            task = yield* fork(reconnectWorker, attemptCount, handleSuccess, options);
        }
    }
}
